import { WaterDrop } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { DecarbContentLayout } from '../components/DecarbContentLayout';
import { DecarbOverviewHeader } from '../components/DecarbOverviewHeader';
import { FilterMenu, SearchBox, SortMenu } from '../components/TechToolbar';
import {
  EnergyKind,
  TechSortDimensions,
  TechSortState,
  WaterPumpWithMetrics,
  getEnergyField,
  sortTechItemsByState,
} from '../types';
import { WaterPumpUnit } from './WaterPumpUnit';

type WaterPumpSelectionDetailsProps = {
  helperTextComponent: React.ReactNode;
  availableWaterPumps: WaterPumpWithMetrics[];
  selectedWaterPumps: (WaterPumpWithMetrics & { count: number })[];
  costEnergyAndCoverageMetrics?: {
    // undefined: no stored figure to show yet (renders the placeholder)
    cost?: number;
    energyDemand: number;
    systemCoveragePercentage: number;
  };
  isLoading?: boolean;
  onAdd: (item: WaterPumpWithMetrics) => void;
  onRemove: (item: WaterPumpWithMetrics & { count: number }) => void;
  onChangeCount: (
    item: WaterPumpWithMetrics & { count: number },
    newCount: number,
  ) => void;
  readOnly?: boolean;
};

export const WaterPumpSelectionDetails: React.FC<
  WaterPumpSelectionDetailsProps
> = ({
  helperTextComponent,
  availableWaterPumps,
  selectedWaterPumps,
  costEnergyAndCoverageMetrics,
  isLoading,
  onAdd,
  onRemove,
  onChangeCount,
  readOnly,
}) => {
  const { cost, energyDemand } = costEnergyAndCoverageMetrics || {};

  const [searchString, setSearchString] = useState('');
  const [sortState, setSortState] = useState<TechSortState>({
    dimension: TechSortDimensions[0],
    direction: 'asc',
  });
  const [filter, setFilter] = useState<EnergyKind[]>([]);

  const availableEnergyTypes = useMemo(() => {
    const types = new Set(
      availableWaterPumps.map((item) => getEnergyField(item)).filter(Boolean),
    );
    return [...types] as EnergyKind[];
  }, [availableWaterPumps]);

  const filteredAndSortedItems = useMemo(() => {
    let items = availableWaterPumps;

    // Apply search filter
    if (searchString.length > 0) {
      items = items.filter((item) =>
        item.name.toLowerCase().includes(searchString.toLowerCase()),
      );
    }

    // Apply energy source filter (include only selected energy types)
    if (filter.length > 0) {
      items = items.filter((item) => {
        const energy = getEnergyField(item);
        return energy ? filter.includes(energy as EnergyKind) : true;
      });
    }

    // Apply sort
    items = sortTechItemsByState(items, sortState);

    return items;
  }, [availableWaterPumps, searchString, filter, sortState]);

  return (
    <DecarbContentLayout
      availableItems={
        readOnly
          ? []
          : filteredAndSortedItems.map((item) => (
              <WaterPumpUnit
                waterPump={item}
                selected={false}
                actions={{ onAdd: () => onAdd(item) }}
              />
            ))
      }
      selectedItems={selectedWaterPumps.map((item) => (
        <WaterPumpUnit
          count={item.count}
          waterPump={item}
          selected={true}
          isLoading={isLoading}
          readOnly={readOnly}
          actions={{
            onRemove: () => onRemove(item),
            onIncrement: () => onChangeCount(item, item.count + 1),
            onDecrement: () => onChangeCount(item, item.count - 1),
            onChangeCount: (newCount: number) => onChangeCount(item, newCount),
          }}
        />
      ))}
      overviewHeader={
        <DecarbOverviewHeader
          systemCoverageName={'Water Pump'}
          systemCoverageIcon={<WaterDrop />}
          systemCoverageHelperText={
            'Choose enough water pumps to get 100% coverage'
          }
          energyDemand={energyDemand}
          capitalCost={cost}
          coveragePercentage={
            costEnergyAndCoverageMetrics?.systemCoveragePercentage
          }
        />
      }
      helperText={helperTextComponent}
      searchComponent={
        readOnly ? undefined : (
          <SearchBox query={searchString} setQuery={setSearchString} />
        )
      }
      filterComponent={
        readOnly ? undefined : availableEnergyTypes.length > 1 ? (
          <FilterMenu
            listOfAllOptions={availableEnergyTypes}
            selectedOptions={filter}
            onReset={() => setFilter([])}
            onSelect={(option: EnergyKind) =>
              setFilter((prev) =>
                prev.includes(option)
                  ? prev.filter((val) => val !== option)
                  : [...prev, option],
              )
            }
          />
        ) : undefined
      }
      sortComponent={
        readOnly ? undefined : (
          <SortMenu
            dimensions={TechSortDimensions}
            activeSort={sortState}
            onSortChange={setSortState}
          />
        )
      }
    />
  );
};
